// Umami Analytics
import { IUser } from '@types'

export interface UmamiPageViewProps {
  hostname: string
  language: string
  referrer: string
  screen: string
  title: string
  url: string
  website: string
}

declare global {
  interface Umami {
    identify: (userId: string, data?: Record<string, any>) => void
    track: {
      (eventName: string, eventData?: object): void
      (callback: (props: UmamiPageViewProps) => UmamiPageViewProps): void
    }
  }

  interface Window {
    umami: Umami
  }
}

export function trackEvent(eventName: string, eventData?: object) {
  window.umami?.track(eventName, eventData)
}

export function identify(user: IUser | undefined) {
  if (!user) {
    return
  }

  window.umami?.identify(user._id, {
    _id: user._id,
    username: user.username,
  })
}

// Helper functions for organized event tracking

// Authentication events
export function trackAuthLogin(success: boolean, error?: string) {
  trackEvent(success ? 'auth_login_success' : 'auth_login_error', {
    error: error || undefined,
  })
}

export function trackAuthRegister(success: boolean, error?: string) {
  trackEvent(success ? 'auth_register_success' : 'auth_register_error', {
    error: error || undefined,
  })
}

export function trackAuthLogout() {
  trackEvent('auth_logout')
}

// Room events
export function trackRoomCreate(success: boolean, error?: string) {
  trackEvent(success ? 'room_create_success' : 'room_create_error', {
    error: error || undefined,
  })
}

export function trackRoomEdit(success: boolean, error?: string) {
  trackEvent(success ? 'room_edit_success' : 'room_edit_error', {
    error: error || undefined,
  })
}

export function trackRoomDelete(success: boolean, error?: string) {
  trackEvent(success ? 'room_delete_success' : 'room_delete_error', {
    error: error || undefined,
  })
}

export function trackRoomSelect() {
  trackEvent('room_select')
}

export function trackRoomSort(sortBy: 'price' | 'name') {
  trackEvent('room_sort_toggle', { sortBy })
}

// Item events
export function trackItemCreate(success: boolean, hasImage: boolean, hasInvoice: boolean, error?: string) {
  trackEvent(success ? 'item_create_success' : 'item_create_error', {
    hasImage,
    hasInvoice,
    error: error || undefined,
  })
}

export function trackItemEdit(success: boolean, hasImageUpdate: boolean, hasInvoiceUpdate: boolean, error?: string) {
  trackEvent(success ? 'item_edit_success' : 'item_edit_error', {
    hasImageUpdate,
    hasInvoiceUpdate,
    error: error || undefined,
  })
}

export function trackItemDelete(success: boolean, error?: string) {
  trackEvent(success ? 'item_delete_success' : 'item_delete_error', {
    error: error || undefined,
  })
}

export function trackItemTransfer(success: boolean, error?: string) {
  trackEvent(success ? 'item_transfer_success' : 'item_transfer_error', {
    error: error || undefined,
  })
}

export function trackItemOpenInvoice(from: string) {
  trackEvent('item_action_open_invoice', { from })
}

export function trackItemImageUpload(isUpdate: boolean) {
  trackEvent('item_action_image_upload', { isUpdate })
}

export function trackItemInvoiceUpload(isUpdate: boolean) {
  trackEvent('item_action_invoice_upload', { isUpdate })
}

// Account events
export function trackAccountUpdate(success: boolean, passwordChanged: boolean, error?: string) {
  trackEvent(success ? 'account_update_success' : 'account_update_error', {
    passwordChanged,
    error: error || undefined,
  })
}

// UI Interaction events
export function trackFormSubmit(formType: 'login' | 'register' | 'account_update') {
  trackEvent('form_submit', { formType })
}
